import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiArrowLeft, FiClock, FiTag, FiUsers, FiCalendar, FiFileText, FiDollarSign, FiSend } from "react-icons/fi";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AuthModal from "../components/cowork/AuthModal";
import MemberShell from "../components/cowork/MemberShell";
import useCoWorkUser from "../hooks/useCoWorkUser";
import useCoWorkBranding from "../hooks/useCoWorkBranding";
import { apiRequest, errorMessage, fetchCoWorkSettings, isAuthenticated } from "../api";
import { budgetText, cardClass, splitList, timeAgo, STATUS_LABELS, STATUS_STYLES } from "../components/cowork/jobUtils";
import { Field, FormAlert, SubmitButton } from "../components/forms/formKit";

export default function CoWorkJobPage() {
  const { id } = useParams();
  const { user, refresh, signOut } = useCoWorkUser();
  const [job, setJob] = useState(undefined);   // undefined = loading, null = not found
  const [settings, setSettings] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ cover_letter: "", bid_amount: "", delivery_days: "7" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadJob = async () => {
    // Send the token when signed in so the server can tell us about our own proposal / ownership.
    const { ok, data } = await apiRequest(`/core/cowork/jobs/${id}/`, { auth: isAuthenticated() });
    setJob(ok ? data : null);
  };

  useEffect(() => {
    fetchCoWorkSettings().then(setSettings).catch(() => {});
  }, []);

  useEffect(() => {
    setJob(undefined);
    loadJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id]);

  useCoWorkBranding(settings);
  useEffect(() => {
    if (job) document.title = `${job.title} - ${settings?.browser_title || "Co-Work"}`;
  }, [job, settings]);

  const currency = settings?.currency_label || "USD";

  const apply = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const { ok, data } = await apiRequest(`/core/cowork/jobs/${id}/apply/`, {
      method: "POST", auth: true,
      body: { cover_letter: form.cover_letter, bid_amount: form.bid_amount, delivery_days: Number(form.delivery_days) },
    });
    setBusy(false);
    if (!ok) return setError(errorMessage(data));
    loadJob();
  };

  let action;
  if (!job) {
    action = null;
  } else if (job.is_owner) {
    action = (
      <div>
        <p className="text-gray-300 mb-4">This is your job. Review proposals and manage it from your dashboard.</p>
        <Link to="/co-work/dashboard" className="inline-block px-6 py-2.5 rounded-full bg-brand-gradient font-semibold">Open Dashboard</Link>
      </div>
    );
  } else if (job.my_proposal) {
    action = (
      <div>
        <p className="text-gray-300">
          You applied to this job. Status:{" "}
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[job.my_proposal.status]}`}>
            {STATUS_LABELS[job.my_proposal.status]}
          </span>
        </p>
        <Link to="/co-work/dashboard" className="inline-block mt-4 px-6 py-2.5 rounded-full bg-brand-gradient font-semibold">View my proposals</Link>
      </div>
    );
  } else if (job.status !== "open") {
    action = <p className="text-gray-400">This job is no longer accepting proposals.</p>;
  } else if (!user) {
    action = (
      <div>
        <p className="text-gray-300 mb-4">Sign in as a seller to apply for this job.</p>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => setModal({ mode: "login", role: "seller" })} className="px-6 py-2.5 rounded-full border border-navy-border hover:border-accent-light">Sign In</button>
          <button onClick={() => setModal({ mode: "register", role: "seller" })} className="px-6 py-2.5 rounded-full bg-brand-gradient font-semibold">Register as Seller</button>
        </div>
      </div>
    );
  } else if (user.role !== "seller") {
    action = <p className="text-gray-400">Only seller accounts can apply for jobs. {user.role === "buyer" ? "You are signed in as a buyer." : ""}</p>;
  } else {
    action = (
      <form onSubmit={apply} className="space-y-5">
        <Field as="textarea" label="Cover letter" required icon={FiFileText} minLength={20} rows={5} value={form.cover_letter} placeholder="Tell the buyer why you are the right person for this job..." onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} />
        <div className="grid grid-cols-2 gap-5">
          <Field label={`Your bid (${currency}${job.budget_type === "hourly" ? " / hour" : ""})`} required icon={FiDollarSign} type="number" min="1" step="0.01" value={form.bid_amount} onChange={(e) => setForm({ ...form, bid_amount: e.target.value })} />
          <Field label="Delivery (days)" required icon={FiClock} type="number" min="1" value={form.delivery_days} onChange={(e) => setForm({ ...form, delivery_days: e.target.value })} />
        </div>
        <FormAlert>{error}</FormAlert>
        <SubmitButton busy={busy} icon={FiSend}>Submit Proposal</SubmitButton>
      </form>
    );
  }

  return (
    <>
      <Header logo={settings?.logo} />
      <div className="h-16" />
      <main className="bg-navy text-white min-h-[70vh] py-10">
        <MemberShell user={user} onSignOut={signOut}>
          <Link to="/co-work" className="inline-flex items-center gap-1 text-accent-light text-sm mb-6 hover:underline">
            <FiArrowLeft /> Back to Co-Work
          </Link>

          {job === undefined && <p className="text-gray-400">Loading...</p>}
          {job === null && (
            <div className="text-center py-20">
              <h1 className="text-3xl font-bold">Job not found</h1>
              <p className="text-gray-400 mt-2">It may have been closed or removed.</p>
            </div>
          )}

          {job && (
            <div className="grid lg:grid-cols-[1fr_340px] gap-8 items-start">
              <div className="space-y-6">
                <div className={`${cardClass} p-7`}>
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[job.status]}`}>{STATUS_LABELS[job.status]}</span>
                    {job.category && <span className="text-sm text-gray-400 flex items-center gap-1"><FiTag size={13} /> {job.category}</span>}
                  </div>
                  <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">{job.title}</h1>
                  <p className="text-sm text-gray-400 mt-3 flex flex-wrap gap-x-5 gap-y-1">
                    <span className="flex items-center gap-1"><FiClock size={13} /> Posted {timeAgo(job.created_at)}</span>
                    <span className="flex items-center gap-1"><FiUsers size={13} /> {job.proposals_count} proposal{job.proposals_count === 1 ? "" : "s"}</span>
                    {job.deadline && <span className="flex items-center gap-1"><FiCalendar size={13} /> Deadline {job.deadline}</span>}
                  </p>
                  <h2 className="text-lg font-semibold mt-7 mb-2">Description</h2>
                  <p className="text-gray-300 whitespace-pre-line leading-relaxed">{job.description}</p>
                  {splitList(job.skills).length > 0 && (
                    <>
                      <h2 className="text-lg font-semibold mt-7 mb-3">Skills needed</h2>
                      <div className="flex flex-wrap gap-2">
                        {splitList(job.skills).map((s) => (
                          <span key={s} className="px-3 py-1 rounded-md bg-white/5 border border-navy-border text-sm text-gray-200">{s}</span>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <div className={`${cardClass} p-7`}>
                  <h2 className="text-xl font-bold mb-4">{job.is_owner || job.my_proposal ? "Your proposal" : "Apply for this job"}</h2>
                  {action}
                </div>
              </div>

              <aside className={`${cardClass} p-6 lg:sticky lg:top-24`}>
                <p className="text-xs uppercase tracking-widest text-white/50 font-semibold">Budget</p>
                <p className="text-2xl font-bold text-accent-light mt-1">{budgetText(job, currency)}</p>
                <p className="text-sm text-gray-400">{job.budget_type === "hourly" ? "Hourly rate" : "Fixed price"}</p>
                <div className="border-t border-navy-border my-5" />
                <p className="text-xs uppercase tracking-widest text-white/50 font-semibold">Posted by</p>
                <p className="font-semibold mt-1">{job.buyer_name}</p>
                {job.buyer_company && <p className="text-sm text-gray-400">{job.buyer_company}</p>}
              </aside>
            </div>
          )}
        </MemberShell>
      </main>
      <Footer />

      {modal && (
        <AuthModal
          initialMode={modal.mode}
          initialRole={modal.role}
          settings={settings}
          onClose={() => setModal(null)}
          onAuthenticated={async () => { setModal(null); await refresh(); }}
        />
      )}
    </>
  );
}
